import { useRecoilCallback } from 'recoil'
import { canvasPageIdAtom, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import {
  editorTextBoxDocumentsSelector,
  editorTextBoxDraftsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { resolveEditorTextBoxDocument } from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { EditorTextSizingMode } from '@/utils/editor-text-box/editorTextBoxAdapter'

/**
 * What a writer changes about a box's text sizing: `sizing` takes one of the
 * two modes, never a legacy value a row may still carry, and the maximum is a
 * number. A key left out is left alone: a change of `sizing` keeps the
 * maximum the row holds (nothing in the database pairs them, and a duplicate
 * of a Shrink box keeps it too). A `maxFontSizePt` of `null` is the person
 * clearing the field — no limit.
 */
export type EditorTextSizingPatch = {
  sizing?: EditorTextSizingMode
  maxFontSizePt?: number | null
}

/**
 * The road for the `sizing` column and the maximum: the drawer's maximum
 * field (`TextSizing.js`, shown only while text scaling is on) writes through
 * it, and so would a spec through the armed e2e probe
 * (`window.__editorSetTextBoxSizing`, set in `ItemControlOverlay.tsx`), though
 * none does today; the write goes straight into the box's draft, which the
 * canvas paints from and `useEditorSaveTextBoxes` sends. The frame's road (the item atom,
 * flushed at save time) is wrong for these two — the paint and the chrome
 * read them off the document, so a value set through the item would not show
 * until Save.
 *
 * A `sizing` or maximum change is a paint change, not a write: the read-only fit
 * runs again under the new sizing and keeps its scale on the paint, and the
 * authored sizes move only under the person's own gesture
 * (`writeFitIntoDraft` is gated on the item's frame differing from the
 * document's). So this raises `unsavedChangesAtom` by hand, as every hook
 * that writes the drafts atom does, and writes nothing else.
 *
 * There is no undo step: both writers act on the selected box from outside
 * the editing host — a mounted host follows the change through
 * `setTextSizing` and pushes nothing — and the drawer's width and height
 * fields record nothing either.
 */
export const useEditorSetTextBoxSizing = () => {
  const handleError = useErrorHandler('useEditorSetTextBoxSizing')

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (boxId: string, patch: EditorTextSizingPatch): Promise<boolean> => {
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) return false
        const [results, drafts] = await Promise.all([
          snapshot.getPromise(editorTextBoxDocumentsSelector(pageId)),
          snapshot.getPromise(editorTextBoxDraftsAtom),
        ])
        // A box the person has not edited has no draft; the row's own
        // document is the base, and the save sends only drafted boxes, so
        // this write is what puts the box in that set.
        const source = drafts[boxId] ?? results?.get(boxId)?.document ?? null
        if (source === null) {
          handleError(
            'The text sizing could not be changed',
            new Error(`no document for box ${boxId}`),
          )
          return false
        }
        const box = {
          ...source.box,
          ...('sizing' in patch ? { sizing: patch.sizing } : {}),
          ...('maxFontSizePt' in patch
            ? { maxFontSizePt: patch.maxFontSizePt ?? undefined }
            : {}),
        }
        let next
        try {
          next = resolveEditorTextBoxDocument({ ...source, box })
        } catch (error) {
          handleError(
            'The text sizing could not be changed',
            error instanceof Error ? error : new Error(String(error)),
          )
          return false
        }
        set(editorTextBoxDraftsAtom, (prev) => ({ ...prev, [boxId]: next }))
        set(unsavedChangesAtom, true)
        return true
      },
    [handleError],
  )
}

export default useEditorSetTextBoxSizing
